import { parseWithZod } from '@conform-to/zod'
import { motion } from 'framer-motion'
import { Img } from 'openimg/react'
import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { data } from 'react-router'
import { z } from 'zod'
import { RichContent } from '#app/components/rich-content.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { type Language } from '#app/i18n.server.ts'
import { fadeInUp } from '#app/utils/animations.ts'
import { getUserId } from '#app/utils/auth.server.ts'
import { OVER_SECTION_IDS as SECTION_IDS } from '#app/utils/content-sections.ts'
import { prisma } from '#app/utils/db.server.ts'
import { cn } from '#app/utils/misc.tsx'
import { buildMeta, metaDescriptionFromContent } from '#app/utils/seo.ts'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { useOptionalUser } from '#app/utils/user.ts'
import { type Route } from './+types/over.ts'

const UpdateParagraphSchema = z.object({
	sectionId: z.string(),
	content: z.string(),
})

// Default content for each section (section IDs live in the shared registry)
const DEFAULT_CONTENT = {
	[SECTION_IDS.INTRO]: `In haar werk onderzoekt Else Ringnalda hoe een ideale samenleving eruit ziet: Een wereld waarin iedereen van waarde is en een waarde in zichzelf heeft. Zij stelt zich een samenleving voor waarin mensen elkaar respecteren en open staan voor elkaars verschillen ongeacht gender, religie, etniciteit of positie in de samenleving. Met haar figuratieve beelden wil zij deze visie tot uiting brengen en toeschouwers uitnodigen om te reflecteren op hun eigen waarde, hun relatie tot anderen en onze natuurlijke omgeving.`,
	[SECTION_IDS.AMSTERDAM_NOTE]: `Als je dit beeld van achteren nadert, zie je dat het een mens is. Het kan een dominee, imam, rabbijn, wit, zwart, man, vrouw zijn. Ieder mens mag zich hierin herkennen.`,
	[SECTION_IDS.INSPIRATION_INTRO]: `Inspiratie voor Else's beelden komt voort uit het idee dat we elkaar accepteren op deze wereld en samenleven in balans met elkaar èn met eerbied voor natuur en milieu.`,
	[SECTION_IDS.BALANCE_NOTE]: `Twee andere beelden hebben in hun titel 'Balans'. Het ene is gemaakt in opdracht van Justitie, zie opdrachten. Het andere is vrij werk waar een vrouw een man op haar schouders draagt.`,
	[SECTION_IDS.THEATER_NOTE]: `In haar werk 'Theater op Water' zijn beelden te zien die onder invloed van wind en stroming bewegen en daardoor een interactie met elkaar aangaan. Er is steeds een andere scène te zien.`,
	[SECTION_IDS.WIND_NOTE]: `We worden geconfronteerd met klimaatproblemen als gevolg van ons handelen, uitgebeeld door het beeld 'Wie wind zaait…'. Het beeld zaait een kei, die in haar hand past. De zaadjes, die achter haar liggen zijn uitgegroeid tot rotsblokken, groter dan zij ooit had kunnen voorzien.`,
	[SECTION_IDS.NARCISSUS_NOTE]: `Met 'Narcissus' verbeeldt zij de menselijke zoektocht naar erkenning en reflectie.`,
	[SECTION_IDS.OPDRACHTEN_INTRO]: `Else Ringnalda werkt in opdracht van gemeenten, bedrijven en particulieren. Haar werk is te vinden in de openbare ruimte, bedrijfscollecties en particuliere collecties. Voor informatie over opdrachten kunt u contact opnemen via het contactformulier.`,
}

export const handle = { i18n: 'common' }

export async function loader({ request }: Route.LoaderArgs) {
	const userId = await getUserId(request)
	const { getLocale } = await import('#app/i18n.server.ts')
	const language = (await getLocale(request)) as Language

	// Load both the requested language and nl (editorial source). A missing
	// translation should fall back to the latest CMS Dutch rather than the
	// stale string baked into DEFAULT_CONTENT.
	const sections = await prisma.contentSection.findMany({
		where: {
			sectionId: { in: Object.values(SECTION_IDS) },
			language: { in: [language, 'nl'] },
		},
	})

	const content: Record<string, string> = {}
	for (const [, sectionId] of Object.entries(SECTION_IDS)) {
		const requested = sections.find(
			(s) => s.sectionId === sectionId && s.language === language,
		)
		const dutch = sections.find(
			(s) => s.sectionId === sectionId && s.language === 'nl',
		)
		content[sectionId] =
			requested?.content ||
			dutch?.content ||
			DEFAULT_CONTENT[sectionId as keyof typeof DEFAULT_CONTENT] ||
			''
	}

	return data({ userId, content, language })
}

export async function action({ request }: Route.ActionArgs) {
	const userId = await getUserId(request)
	if (!userId) {
		return data({ error: 'Unauthorized' }, { status: 401 })
	}

	const { getLocale } = await import('#app/i18n.server.ts')
	const language = (await getLocale(request)) as Language
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: UpdateParagraphSchema,
	})

	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { sectionId, content } = submission.value

	// Save the current language and machine-translate to the others. Rows an
	// admin corrected by hand in /admin/content (manuallyEdited) are skipped.
	const { createContentSectionTranslations } = await import(
		'#app/utils/translation.server.ts'
	)
	await createContentSectionTranslations(sectionId, content, language)

	return redirectWithToast('/over', {
		type: 'success',
		title: 'Tekst bijgewerkt',
		description: 'De tekst is succesvol bijgewerkt en vertaald.',
	})
}

// Editable paragraph component
function EditableParagraph({
	sectionId,
	children,
	isLoggedIn,
	className = '',
}: {
	sectionId: string
	children: string
	isLoggedIn: boolean
	className?: string
}) {
	const [isHovered, setIsHovered] = useState(false)
	const [clickedParagraph, setClickedParagraph] = useState<{
		sectionId: string
		content: string
	} | null>(null)
	const paragraphRef = useRef<HTMLDivElement>(null)

	const handleClick = () => {
		if (isLoggedIn) {
			setClickedParagraph({ sectionId, content: children })
		}
	}

	// Listen for AI assistant events
	useEffect(() => {
		const handleAIUpdate = (event: CustomEvent) => {
			if (event.detail.sectionId === sectionId && event.detail.content) {
				// Content has been updated via AI
				setClickedParagraph(null)
				// Reload the page to show updated content
				window.location.reload()
			}
		}

		window.addEventListener('ai-paragraph-update' as any, handleAIUpdate)
		return () => {
			window.removeEventListener('ai-paragraph-update' as any, handleAIUpdate)
		}
	}, [sectionId])

	return (
		<>
			<div
				className={cn('relative', isLoggedIn && 'cursor-pointer')}
				onMouseEnter={() => isLoggedIn && setIsHovered(true)}
				onMouseLeave={() => isLoggedIn && setIsHovered(false)}
				onClick={handleClick}
			>
				{/* Background highlight */}
				{isLoggedIn && isHovered && (
					<div className="absolute inset-0 -mx-2 -my-1 rounded-lg bg-blue-50 transition-all duration-200 dark:bg-blue-950/20" />
				)}

				{/* Paragraph content */}
				<div
					ref={paragraphRef}
					className={cn(className, 'relative z-10 transition-all duration-200')}
				>
					<RichContent html={children} />
					{isLoggedIn && isHovered && (
						<Icon
							name="pencil-2"
							className="absolute top-0 right-0 h-4 w-4 text-blue-600 dark:text-blue-400"
						/>
					)}
				</div>
			</div>
			{clickedParagraph && (
				<EditParagraphDialog
					sectionId={clickedParagraph.sectionId}
					content={clickedParagraph.content}
					onClose={() => setClickedParagraph(null)}
				/>
			)}
		</>
	)
}

// Edit dialog that triggers AI assistant
function EditParagraphDialog({
	sectionId,
	content,
	onClose,
}: {
	sectionId: string
	content: string
	onClose: () => void
}) {
	useEffect(() => {
		// Trigger AI assistant with edit paragraph flow
		const event = new CustomEvent('trigger-ai-edit-paragraph', {
			detail: { sectionId, content },
		})
		window.dispatchEvent(event)
		onClose()
	}, [sectionId, content, onClose])

	return null
}

export default function OverRoute({ loaderData }: Route.ComponentProps) {
	const { content } = loaderData
	const user = useOptionalUser()
	const isLoggedIn = Boolean(user)
	const { t } = useTranslation()

	const cvEducation = [
		{ years: '1984–1985', school: 'Gerrit Rietveld Academie', city: 'Amsterdam' },
		{ years: '1979–1984', school: 'Hogeschool voor de Kunsten Utrecht', city: 'Utrecht' },
	]
	const cvPublicWorks = [
		{ year: 2021, title: 'Voorbij', location: 'Brussel, BE' },
		{ year: 2017, title: 'Ode aan de boom', location: 'Doorn' },
		{ year: 2016, title: 'Libelle / Empty Nest', location: 'Beveren, BE' },
		{ year: 2004, title: 'Samen Een', location: 'Steenwijk' },
		{ year: 2002, title: 'Balans', location: 'NL Juristenvereniging' },
		{ year: 1993, title: 'Flipje', location: 'Tiel' },
		{ year: 1989, title: 'De Wilg', location: 'Utrecht' },
		{ year: 1987, title: 'Wie wind zaait…', location: 'Deventer' },
		{ year: 1986, title: 'Figuratief beeld', location: 'Amsterdam' },
	]

	return (
		<div>
			{/* Hero portrait */}
			<section className="relative overflow-hidden" style={{ height: '80vh', minHeight: 560 }}>
				<Img
					src="/img/else-ringnalda-hero.jpg"
					alt="Atelier"
					className="h-full w-full object-cover"
					width={1920}
					height={1080}
					fit="cover"
					isAboveFold
				/>
				<div
					className="absolute inset-0 flex items-end"
					style={{
						padding: 'clamp(32px, 6vw, 80px)',
						background: 'linear-gradient(180deg, transparent 50%, rgba(14,20,32,0.4))',
						color: 'var(--paper)',
					}}
				>
					<div>
						<div className="eyebrow mb-3" style={{ color: 'rgba(250,248,244,0.8)' }}>
							{t('visualArtist')}
						</div>
						<h1 className="display" style={{ fontSize: 'clamp(48px, 8vw, 112px)' }}>
							Else<br />Ringnalda
						</h1>
					</div>
				</div>
			</section>

			{/* Bio */}
			<motion.section
				className="py-[120px]"
				initial="initial"
				whileInView="animate"
				viewport={{ once: true, amount: 0.15 }}
				variants={fadeInUp}
			>
				<div className="container">
					<div className="grid gap-20 md:grid-cols-[1fr_2fr]">
						<div className="eyebrow">{t('aboutElseRingnalda')}</div>
						<div className="flex flex-col gap-6">
							<EditableParagraph
								sectionId={SECTION_IDS.INTRO}
								isLoggedIn={isLoggedIn}
								className="lede"
								children={content[SECTION_IDS.INTRO] || ''}
							/>
							<EditableParagraph
								sectionId={SECTION_IDS.AMSTERDAM_NOTE}
								isLoggedIn={isLoggedIn}
								className="lede"
								children={content[SECTION_IDS.AMSTERDAM_NOTE] || ''}
							/>
						</div>
					</div>
				</div>
			</motion.section>

			{/* Inspiration */}
			<motion.section
				className="py-20"
				style={{ background: 'var(--paper-2)' }}
				initial="initial"
				whileInView="animate"
				viewport={{ once: true, amount: 0.15 }}
				variants={fadeInUp}
			>
				<div className="container">
					<div className="grid items-center gap-16 md:grid-cols-2">
						<Img
							src="/img/delicate-balance.jpg"
							alt="Delicate Balance"
							className="w-full object-cover"
							width={600}
							height={750}
							fit="cover"
							style={{ aspectRatio: '4/5' }}
						/>
						<div>
							<div className="eyebrow mb-4">{t('inspiration')}</div>
							<h2 className="display mb-6" style={{ fontSize: 'clamp(32px, 5vw, 64px)' }}>
								{t('aboutPlayingHuman')}
							</h2>
							<EditableParagraph
								sectionId={SECTION_IDS.INSPIRATION_INTRO}
								isLoggedIn={isLoggedIn}
								className="lede"
								children={content[SECTION_IDS.INSPIRATION_INTRO] || ''}
							/>
						</div>
					</div>
				</div>
			</motion.section>

			{/* Themes — thematic notes about individual works */}
			<motion.section
				className="py-[120px]"
				initial="initial"
				whileInView="animate"
				viewport={{ once: true, amount: 0.15 }}
				variants={fadeInUp}
			>
				<div className="container">
					<div className="grid gap-20 md:grid-cols-[1fr_2fr]">
						<div className="eyebrow">{t('aboutThemes')}</div>
						<div className="grid gap-x-16 gap-y-14 md:grid-cols-2">
							{[
								// Headings are artwork titles (proper names, untranslated)
								{ title: 'Balans', id: SECTION_IDS.BALANCE_NOTE },
								{ title: 'Theater op Water', id: SECTION_IDS.THEATER_NOTE },
								{ title: 'Wie wind zaait…', id: SECTION_IDS.WIND_NOTE },
								{ title: 'Narcissus', id: SECTION_IDS.NARCISSUS_NOTE },
							].map((theme) => (
								<div key={theme.id}>
									<h3 className="font-heading mb-3 text-xl font-medium italic">
										{theme.title}
									</h3>
									<EditableParagraph
										sectionId={theme.id}
										isLoggedIn={isLoggedIn}
										className="text-ink-soft text-[15px] leading-relaxed"
										children={content[theme.id] || ''}
									/>
								</div>
							))}
						</div>
					</div>
				</div>
			</motion.section>

			{/* Commissions */}
			<motion.section
				className="py-20"
				style={{ background: 'var(--paper-2)' }}
				initial="initial"
				whileInView="animate"
				viewport={{ once: true, amount: 0.15 }}
				variants={fadeInUp}
			>
				<div className="container">
					<div className="grid gap-20 md:grid-cols-[1fr_2fr]">
						<div className="eyebrow">{t('aboutCommissions')}</div>
						<EditableParagraph
							sectionId={SECTION_IDS.OPDRACHTEN_INTRO}
							isLoggedIn={isLoggedIn}
							className="lede"
							children={content[SECTION_IDS.OPDRACHTEN_INTRO] || ''}
						/>
					</div>
				</div>
			</motion.section>

			{/* CV */}
			<motion.section
				className="py-[120px]"
				initial="initial"
				whileInView="animate"
				viewport={{ once: true, amount: 0.15 }}
				variants={fadeInUp}
			>
				<div className="container">
					<h2 className="display mb-16" style={{ fontSize: 'clamp(32px, 5vw, 64px)' }}>
						{t('curriculumVitae')}
					</h2>

					{/* Education */}
					<div className="mb-20">
						<div className="eyebrow mb-6">{t('education')}</div>
						<table className="w-full border-collapse">
							<tbody>
								{cvEducation.map((e, i) => (
									<tr key={i} style={{ borderBottom: '1px solid var(--line)' }}>
										<td className="font-heading py-5 text-lg italic" style={{ width: 160 }}>{e.years}</td>
										<td className="py-5 text-base">{e.school}</td>
										<td className="py-5 text-right text-sm" style={{ color: 'var(--muted-gallery)' }}>{e.city}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{/* Public works */}
					<div>
						<div className="eyebrow mb-6">{t('publicArt')}</div>
						<table className="w-full border-collapse">
							<tbody>
								{cvPublicWorks.map((p, i) => (
									<tr key={i} style={{ borderBottom: '1px solid var(--line)' }}>
										<td className="font-heading py-5 text-lg italic" style={{ width: 120 }}>{p.year}</td>
										<td className="py-5 text-base">{p.title}</td>
										<td className="py-5 text-right text-sm" style={{ color: 'var(--muted-gallery)' }}>{p.location}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</motion.section>
		</div>
	)
}

export const meta: Route.MetaFunction = ({ data }) => {
	// Prefer the CMS intro (in the request's language) over the static string.
	const intro = data?.content?.[SECTION_IDS.INTRO]
	return buildMeta({
		title: 'Over Else Ringnalda',
		description:
			(intro && metaDescriptionFromContent(intro)) ||
			'Beeldend kunstenaar Else Ringnalda. Werkend in brons en steen sinds 1986. Lees over haar achtergrond, inspiratie en curriculum vitae.',
		url: '/over',
		locale: data?.language,
	})
}
